import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Users, Plus, Trash, PencilSimple, User, ArrowRight } from '@phosphor-icons/react';
import { WatchHowLink } from '../components/WatchHowLink';

export default function GroupsPage() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupDetail, setGroupDetail] = useState(null);

  const fetchGroups = async () => {
    try {
      const { data } = await api.get('/groups');
      setGroups(data);
    } catch (err) {
      console.error('Failed to load groups', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGroups(); }, []);

  const openCreate = () => {
    setEditingGroup(null);
    setForm({ name: '', description: '' });
    setDialogOpen(true);
  };

  const openEdit = (group) => {
    setEditingGroup(group);
    setForm({ name: group.name, description: group.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editingGroup) {
        await api.put(`/groups/${editingGroup.group_id}`, form);
      } else {
        await api.post('/groups', form);
      }
      setDialogOpen(false);
      fetchGroups();
    } catch (err) {
      console.error('Save failed', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('Delete this group?')) return;
    try {
      await api.delete(`/groups/${groupId}`);
      if (selectedGroup === groupId) { setSelectedGroup(null); setGroupDetail(null); }
      fetchGroups();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const loadGroupDetail = async (groupId) => {
    setSelectedGroup(groupId);
    try {
      const { data } = await api.get(`/groups/${groupId}`);
      setGroupDetail(data);
    } catch (err) {
      console.error('Failed to load group', err);
    }
  };

  const removeMember = async (contactId) => {
    if (!selectedGroup) return;
    try {
      await api.delete(`/groups/${selectedGroup}/members`, { data: { contact_ids: [contactId] } });
      loadGroupDetail(selectedGroup);
      fetchGroups();
    } catch (err) {
      console.error('Remove failed', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#0B5394] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="groups-page">
      {/* Toolbar */}
      <div className="bg-gradient-to-b from-[#E8E8E8] to-[#D0D0D0] border-b border-[#999] px-3 py-1.5 flex items-center gap-2 shrink-0">
        <Users size={16} weight="bold" className="text-[#0B5394]" />
        <span className="text-[13px] font-bold text-[#333]">Groups</span>
        <WatchHowLink slug="groups" className="ml-1" />
        <div className="flex-1" />
        <button
          onClick={openCreate}
          className="act-toolbar-btn"
          data-testid="create-group-btn"
        >
          <Plus size={14} weight="bold" />
          <span className="text-[10px]">New Group</span>
        </button>
      </div>

      <div className="flex-1 overflow-auto p-2 bg-[#E8E8E8]">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 h-full">
          {/* Groups List */}
          <div className="bg-[#F5F5F0] border border-[#999]" data-testid="groups-list">
            <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999]">
              <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">My Groups</h3>
            </div>
            {groups.length === 0 ? (
              <p className="text-[12px] text-[#999] text-center py-8">No groups yet. Click New Group to create one.</p>
            ) : (
              <div className="divide-y divide-[#DDD]">
                {groups.map(g => (
                  <div
                    key={g.group_id}
                    className={`px-3 py-2 cursor-pointer flex items-center justify-between group transition-colors ${selectedGroup === g.group_id ? 'bg-[#0B5394] text-white' : 'hover:bg-[#E8E8E8]'}`}
                    onClick={() => loadGroupDetail(g.group_id)}
                    data-testid={`group-item-${g.group_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className={`text-[12px] font-medium truncate flex items-center gap-1 ${selectedGroup === g.group_id ? 'text-white' : 'text-[#333]'}`}>
                        {g.group_type === 'smart' && (
                          <span
                            className={`text-[9px] px-1 py-0.5 rounded font-bold ${selectedGroup === g.group_id ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-800'}`}
                            title="Smart group — query re-runs on open"
                          >
                            ⚡SMART
                          </span>
                        )}
                        <span className="truncate">{g.name}</span>
                      </p>
                      <p className={`text-[10px] ${selectedGroup === g.group_id ? 'text-white/70' : 'text-[#999]'}`}>{g.member_count} contact{g.member_count !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                      <button onClick={(e) => { e.stopPropagation(); openEdit(g); }} className={`p-0.5 ${selectedGroup === g.group_id ? 'hover:bg-white/20' : 'hover:bg-gray-200'}`}>
                        <PencilSimple size={11} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(g.group_id); }} className="p-0.5 text-[#C0392B] hover:bg-red-50">
                        <Trash size={11} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Group Detail */}
          <div className="lg:col-span-2 bg-[#F5F5F0] border border-[#999]" data-testid="group-detail">
            <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999] flex items-center justify-between">
              <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">
                {groupDetail ? groupDetail.name : 'Select a Group'}
              </h3>
              {groupDetail && (
                <span className="text-[10px] text-[#666]">{groupDetail.member_count} members</span>
              )}
            </div>
            {!groupDetail ? (
              <p className="text-[12px] text-[#999] text-center py-12">Select a group from the list to see its members</p>
            ) : groupDetail.members.length === 0 ? (
              <p className="text-[12px] text-[#999] text-center py-12">No contacts in this group. Add contacts from the Contact card's Groups tab.</p>
            ) : (
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-[#E0E0E0] border-b border-[#999]">
                    <th className="px-2 py-1.5 text-left font-semibold text-[#333]">Contact</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-[#333]">Company</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-[#333]">Email</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-[#333]">Phone</th>
                    <th className="px-2 py-1.5 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {groupDetail.members.map(m => (
                    <tr key={m.contact_id} className="border-b border-[#DDD] hover:bg-[#E8E8E8]" data-testid={`group-member-${m.contact_id}`}>
                      <td className="px-2 py-1.5">
                        <button onClick={() => navigate(`/contacts/${m.contact_id}`)} className="text-[#0B5394] hover:underline font-medium">
                          {m.first_name} {m.last_name}
                        </button>
                      </td>
                      <td className="px-2 py-1.5 text-[#666]">{m.company || '—'}</td>
                      <td className="px-2 py-1.5 text-[#666]">{m.email || '—'}</td>
                      <td className="px-2 py-1.5 text-[#666]">{m.phone || '—'}</td>
                      <td className="px-1 py-1.5">
                        <button onClick={() => removeMember(m.contact_id)} className="text-[#C0392B] hover:bg-red-50 p-0.5" data-testid={`remove-member-${m.contact_id}`}>
                          <Trash size={11} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Group Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[380px] rounded-none border-[#999] p-0 gap-0" data-testid="group-dialog">
          <DialogHeader className="px-4 pt-3 pb-2 bg-[#E0E0E0] border-b border-[#999]">
            <DialogTitle className="text-[13px] font-bold text-[#333]">
              {editingGroup ? 'Edit Group' : 'New Group'}
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 space-y-3">
            <div>
              <label className="text-[11px] font-medium text-[#333]">Group Name</label>
              <input
                autoFocus
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                autoComplete="one-time-code"
                className="w-full h-[28px] px-2 text-[12px] border border-[#B0B0B0] mt-1 focus:outline-none focus:border-[#0B5394]"
                data-testid="group-name-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-[#333]">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
                className="w-full px-2 py-1 text-[12px] border border-[#B0B0B0] mt-1 focus:outline-none focus:border-[#0B5394] resize-none"
                data-testid="group-desc-input"
              />
            </div>
          </div>
          <div className="flex justify-end px-4 py-2 bg-[#E0E0E0] border-t border-[#999]">
            <button onClick={handleSave} disabled={saving || !form.name.trim()} className="px-3 py-1 text-[11px] font-medium bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50" data-testid="save-group-btn">
              {saving ? '...' : 'OK'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
